import { useId } from 'react';
import { NumericFormat } from 'react-number-format';

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  helperText?: string;
  min?: number;
  max?: number;
  step?: number;
  currency?: string;
  showProgress?: boolean;
  onBlur?: () => void;
}

export function CurrencyInput({
  value,
  onChange,
  label,
  error,
  required = false,
  disabled = false,
  placeholder = '15 000 €',
  className = '',
  helperText,
  min = 0,
  max = 100000,
  step = 100,
  currency = '€',
  showProgress = true,
  onBlur,
}: CurrencyInputProps) {
  const id = useId();
  const formatNumber = (num: number): string =>
    num.toLocaleString('fr-FR');

  const handleBlur = () => {
    if (value > 0) {
      const rounded = Math.round(value / step) * step;
      const clamped = Math.min(max, Math.max(min, rounded));
      if (clamped !== value) onChange(clamped);
    }
    onBlur?.();
  };

  const progressPercentage = Math.min(100, (value / max) * 100);

  return (
    <div className="field-group">
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="form-label-required">*</span>}
        </label>
      )}

      <NumericFormat
        id={id}
        className={`input-field ${error ? 'input-error' : ''} ${className}`}
        value={value || ''}
        onValueChange={(vals) => {
          const num = vals.floatValue ?? 0;
          onChange(Math.min(max, Math.max(0, num)));
        }}
        onBlur={handleBlur}
        thousandSeparator=" "
        decimalSeparator=","
        suffix={` ${currency}`}
        decimalScale={0}
        allowNegative={false}
        placeholder={placeholder}
        type="text"
        inputMode="numeric"
        disabled={disabled}
        autoComplete="off"
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
      />

      {/* Barre de progression */}
      {showProgress && value > 0 && value <= max && !error && (
        <div className="flex items-center gap-2 text-xs">
          <div className="flex-1 bg-neutral-200 rounded-full h-1.5">
            <div
              className="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <span className="text-neutral-600 font-medium min-w-0">
            {formatNumber(value)} {currency}
          </span>
        </div>
      )}

      {helperText && !error && (
        <p className="text-xs text-neutral-500">{helperText}</p>
      )}

      {error && <p id={`${id}-error`} className="error-message">{error}</p>}

      {!error && (value < min || value > max) && value > 0 && (
        <p className="text-xs text-warning-700">
          Montant autorisé : {formatNumber(min)} {currency} – {formatNumber(max)} {currency}
        </p>
      )}
    </div>
  );
}
